<template>
  <InfuseModal :title="t('title')" @close="$emit('close')">
    <InfuseField :label="t('field.capabilities')" size="small">
      <EvseConnector
        v-if="configConnector"
        v-model="configConnector"
        :evseId="connector.connectorId"
        :hide-capabilities="true"
        :removable="false"
        class="w-[467px]"
      />
    </InfuseField>

    <template #actions>
      <InfuseButton data-testid="modal-confirm-button" type="submit" @click="save">{{ t('save') }}</InfuseButton>
    </template>
  </InfuseModal>
</template>

<script lang="ts" setup>
import { ConnectorType, Current } from '@infuse/api/cpms/config';
import { Connector, Evse } from '@infuse/cpms/charging-station/store';
import { EvseConnector, FlatConnector } from '@infuse/cpms/config/ui';

import { InfuseButton, InfuseField, InfuseModal } from '@infuse/ui/components';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ConnectorRoamingSettings } from './types/RoamingSettings';

interface Props {
  connector: Connector;
  evse: Evse;
}
const props = defineProps<Props>();

interface Emits {
  (event: 'close'): void;
  (event: 'save', updatedRoamingSettings: ConnectorRoamingSettings): void;
}
const emit = defineEmits<Emits>();

const { t } = useI18n();

const configConnector = ref<FlatConnector>();

onMounted(() => {
  // Initialize with the current settings.
  configConnector.value = {
    evseCapabilities: [],
    evseIdentifier: props.evse.ocppEvseId,
    connectorType: props.connector.type || ConnectorType.UNSPECIFIED,
    current: props.connector.current || 0,
    maxAmperage: props.connector.maxAmp || 0,
    maxElectricPower: (props.connector.maxElectricPower || 0) / 1000,
    phases: props.connector.phase || 0,
    physicalReference: props.connector.physicalReference,
    voltage: props.connector.voltage || 0
  };
});

const save = () => {
  if (!configConnector.value) {
    return;
  }

  emit('save', {
    connectorType: configConnector.value.connectorType || ConnectorType.UNSPECIFIED,
    current: configConnector.value.current || Current.UNSPECIFIED,
    evseId: props.evse.ocppEvseId,
    maxAmperage: configConnector.value.maxAmperage || 0,
    maxElectricPower: (configConnector.value.maxElectricPower || 0) * 1000,
    phase: configConnector.value.phases || 0,
    physicalReference: configConnector.value.physicalReference || undefined,
    voltage: configConnector.value.voltage || 0
  });
};
</script>

<i18n>
{
  "en": {
    "field": {
      "capabilities": "Capabilities"
    },
    "title": "Edit connector's roaming settings",
    "save": "Save"
  },
  "nl": {
    "field": {
      "capabilities": "Functies"
    },
    "title": "Roaming-instellingen aansluiting aanpassen",
    "save": "Opslaan"
  }
}
</i18n>
